import {
  CalendarOutline,
  LanguageOutline,
  LinearCloseCirlce,
  MessageQuestion,
  Slash,
} from '../../icons';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../../context/PageContext';
import { useLocation, useParams } from 'react-router-dom';
import AddEditTransactionHeader from './addEditTransactionHeader';
import {
  LOCAL_STORAGE_NAMES,
  MAX_FILE_SIZE,
  MAX_LIMIT_VALUE_TRANSACTION,
  ROUTES,
  SystemConstants,
  TransactionPostingPeriodStatus,
  UserType,
} from '../../../constants/constants';
import { useTransaction } from '../../../context/TransactionContext';
import { useLanguage } from '../../../context/LanguageContext';
import dayjs from 'dayjs';
import { useFetchTransactionById } from '../../../hooks/useFetchTransactionById';
import { useFetchTransactionPostingPeriod } from '../../../hooks/useFetchTransactionPostingPeriod';
import { ITransaction } from '../../../interface/transaction';
import { useUser } from '../../../context/UserContext';
import { showToast } from '../../utils/toast';
import InfoMessage from './infoMessage';
import InformativeBanner from './informativeBanner';
import { useCopyTransactionBanner } from './useCopyTransactionBanner';
import FieldErrorMessage from '../../fieldErrorMessage/fieldErrorMessage';
import TextAreaField from '../../textAreaField/textAreaField';
import { InvoiceableType } from '../../../constants/matter';
import { TransactionUnitPriceSource } from '../../../constants/transaction';
import { useFetchMatterParameters } from '../../../hooks/useFetchMatterParameters';
import {
  areStringsEqual,
  displayLanguageName,
  formatter,
  generateEmlFromMail,
  generateEmlWithoutAttachments,
} from '../../../constants/utilityFunctions';
import {
  getHoursStep,
  handleNumericFieldKeyDown,
  handleNumericFieldPaste,
  parseRegionalNumber,
  PRICE_STEP,
} from '../../../helpers/numberFieldHelper';
import TransactionPostingPeriod from './transactionPostingPeriod';
import TransactionTypeSelect from './transactionTypeSelect';
import MatterSelect from './matterSelect';
import SkillSelect from './skillSelect';
import MatterPhaseSelect from './matterPhaseSelect';
import { useEmailCategoryTagger } from '../../../hooks/useEmailCategoryTagger';
import { useCreateNote } from '../../../hooks/useCreateNote';
import { INote } from '../../../interface/notes';
import { useCreateNoteAttachment } from '../../../hooks/useCreateNoteAttachment';
import {
  Button,
  FieldMessageStatus,
  fieldStatusClassName,
  fieldStatusValidation,
  Form,
  FormItem,
  Heading,
  InputNumber,
  Spin,
  Switch,
  Text,
} from '../../Ant';
import { Flex } from 'antd';
import { useFetchMatterPhase } from '../../../hooks/useFetchMatterPhase';
import { useGetUserSkill } from '../../../hooks/useGetUserSkill';
import { ITimer } from '../../../interface/timer';
import {
  checkAttachmentSizeCompliance,
  handleHourPriceRounding,
  TransactionUnitPriceSourceTranslationMapping,
} from '../../../constants/transactionUtility';
import { SignificanceTypeValue } from '../../../constants/notes';
import CustomModal from '../../informationPopup/customModal';
import ApiErrorModal from '../../informationPopup/apiErrorModal';
import CustomDatePicker from '../../Ant/CustomDatePicker/CustomDatePicker';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { ParameterEntityType } from '../../../interface/parameters';
import { useFetchUserParameters } from '../../../hooks/useFetchUserParameters';
import { AnalyticsPage } from '../../../interface/analytics';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useTransactionTypeDefaulting } from './useTransactionTypeDefaulting';
import { useServerUnitPriceHandling } from './useServerUnitPriceHandling';
import { useTransactionTypeValidation } from './useTransactionTypeValidation';
import { useTransactionFormHandling } from './useTransactionFormHandling';
import { useSaveShortcut } from './useSaveShortcut';
import type { InvalidTransactionField } from './useTransactionValidation';
import { useTransactionFieldError } from './useTransactionFieldError';
import { useGraphAuthStatus } from '../../../hooks/useGraphAuthStatus';
import { useMatterRestrictionWarnings } from '../../../hooks/useMatterRestrictionWarnings';
import { FormCard } from '../../../assets/styles/components/formCard.styled';
import { isTransactionFormChanged } from './isTransactionFormChanged';
import PinnedSaveButton from '../../pinnedSaveButton/pinnedSaveButton';
import { getTransactionFormEntryMarkers } from './transactionFormEntryMarkers';

const describedByError = (errorMessage: string | null, errorId: string) =>
  errorMessage ? errorId : undefined;

const AddTransaction = () => {
  const { transactionId } = useParams();
  const { user, userDetails } = useUser();
  const { trackPage } = useAnalytics();
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true ||
    userDetails?.userType === UserType.RESTRICTED;
  const { t } = useTranslation();
  const location = useLocation();
  const { transaction, updateTransaction, initialTransaction } = useTransaction();
  const { data: parametersDetails, isLoading: isParametersLoading } = useFetchUserParameters({
    parametersToGet: `${ParameterEntityType.TRANSACTION},${ParameterEntityType.TRANSACTION_POSTING_PERIOD}`,
  });
  const parameters = parametersDetails?.parameters?.transactionParameters ?? {};
  const { getAppLanguageCode } = useLanguage();
  const { push, goBack } = useNavigationStack();
  const { setTitle } = usePage();
  const [transactionDate, setTransactionDate] = useState<string>('');
  const [checkTransactionPostingPeriod, setCheckTransactionPostingPeriod] =
    useState<boolean>(false);
  const [billableFlag, setBillableFlag] = useState<boolean>(
    transaction?.matter?.invoiceableType === InvoiceableType.billable || !transactionId,
  );
  const formCardRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmationForLargeAttachmentsModal, setConfirmationForLargeAttachmentsModal] =
    useState<boolean>(false);
  const [showFileSizeWarning, setShowFileSizeWarning] = useState<boolean>(false);
  const [billableHours, setBillableHours] = useState<string>(
    Number(transaction?.billableQuantity ?? 0).toFixed(2) ?? '',
  );
  const [totalPrice, setTotalPrice] = useState<string>(
    Number(transaction?.totalPrice ?? 0).toFixed(2) ?? '',
  );
  const [workedHours, setWorkedHours] = useState<string>(
    Number(transaction?.workedHours ?? 0)?.toFixed(2) ?? '',
  );
  const [unitPrice, setUnitPrice] = useState<string>(
    Number(transaction?.unitPrice ?? 0)?.toFixed(2),
  );
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const [showNoteAttachmentErrorModal, setShowNoteAttachmentErrorModal] = useState(false);
  const [noteAttachmentErrorMessage, setNoteAttachmentErrorMessage] = useState('');
  const isTransactionTypeRequired =
    parameters?.transactionTypeRequired && parameters?.transactionTypeInUse;
  const isTransactionTypeHourBased =
    !transaction?.transactionType ||
    areStringsEqual(
      transaction?.transactionType?.unit?.id ?? transaction?.unit?.id ?? transaction?.unitId,
      SystemConstants.UNIT_HOUR,
    );
  const currentDate = dayjs().format('YYYY-MM-DD');

  const { data: matterTransactionParameters } = useFetchMatterParameters({
    matterId: transaction?.matter?.id ?? '',
    enabled: transaction?.matter?.id !== undefined,
    parametersToGet: ['transaction'],
  });
  const matterTransactionParameterDetail =
    matterTransactionParameters?.parameters?.transactionParameters;
  const hoursStep = getHoursStep(
    matterTransactionParameterDetail?.invoiceableDurationStep ??
      parameters?.invoiceableDurationStep,
  );
  const billableStep = isTransactionTypeHourBased ? hoursStep : PRICE_STEP;
  const numericFieldKeyDown =
    (step: number, onStep: (value: string) => void) =>
    (event: React.KeyboardEvent<HTMLInputElement>) =>
      handleNumericFieldKeyDown(event, { step, onStep });

  // Use extracted hooks
  useTransactionTypeDefaulting(parameters);
  useServerUnitPriceHandling({
    transactionDate,
    isTransactionTypeHourBased,
    matterTransactionParameterDetail,
    parameters,
    setUnitPrice,
    setBillableHours,
    setTotalPrice,
    setWorkedHours,
  });
  useTransactionTypeValidation();
  const { showCopyTransactionBanner, copyTransactionBannerMessage, dismissCopyTransactionBanner } =
    useCopyTransactionBanner();
  const { data: transactionDetails, isLoading: isTransactionLoading } = useFetchTransactionById({
    id: !transaction ? (transactionId ?? '') : '',
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'transactionUser:fullName',
      'transactionType:translations,standardPrice,ignoreDiscount',
      'skill:name',
      'unit:translations',
      'matterPhase',
    ],
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'workedHours',
      'unitId',
      'transactionTypeId',
      'matterPhaseId',
      'transactionUserId',
      'priceSource',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'hasImportantNotes',
      'totalPriceSetManually',
    ],
  });
  const loadedTransactionDate = transactionId ? (initialTransaction?.transactionDate ?? '') : '';
  const shouldCheckPostingPeriodLock =
    Boolean(
      parametersDetails?.parameters?.transactionPostingPeriodParameters?.postingPeriodInUse,
    ) &&
    user?.userType !== UserType.ADMIN &&
    !transaction?.isSubscriptionBased &&
    !!loadedTransactionDate;
  const { data: loadedPostingPeriod, isLoading: isPostingPeriodLoading } =
    useFetchTransactionPostingPeriod(
      {
        extraFilters: {
          weekStartDate: `$lte:${dayjs(loadedTransactionDate).format('YYYY-MM-DD')}`,
          weekEndDate: `$gte:${dayjs(loadedTransactionDate).format('YYYY-MM-DD')}`,
        },
      },
      shouldCheckPostingPeriodLock,
    );
  const isPostingPeriodLocked =
    loadedPostingPeriod?.data?.[0]?.status === TransactionPostingPeriodStatus.LOCKED;
  const entryMarkers = getTransactionFormEntryMarkers({
    transactionTypeInUse: parameters?.transactionTypeInUse,
    isPostingPeriodLocked,
    isParametersLoading,
    isPostingPeriodLoading,
  });
  const lockedPeriodWarningMessage =
    isPostingPeriodLocked && loadedTransactionDate
      ? t('transaction:postingPeriod.periodIsLockedOnlySubject', {
          transactionDate: formatter.dateStandard(new Date(loadedTransactionDate)),
        })
      : undefined;

  const skipPostingPeriodCheck =
    !!transactionId && isPostingPeriodLocked && user?.userType !== UserType.ADMIN;
  const { data: matterPhaseList } = useFetchMatterPhase({
    sortBy: ['name:ASC'],
    pageSize: 50,
    search: '',
    matterId: transaction?.matter?.id ?? undefined,
  });
  const isMatterPhaseLocked = matterPhaseList?.length === 0 || isPostingPeriodLocked;
  const isSkillRequiredOrUnknown = isParametersLoading || Boolean(parameters?.skillRequired);
  const { data: suggestedSkill } = useGetUserSkill({
    userId: user?.id,
    matterId: transaction?.matter?.id,
  });
  const createNoteMutation = useCreateNote();
  const createNoteAttachmentMutation = useCreateNoteAttachment();
  const { untagEmailCategory, tagEmailWithCategory, loading, getItemId } = useEmailCategoryTagger();
  const { needsReauth } = useGraphAuthStatus();
  const unitTranslationName =
    transaction?.unit?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? '-';
  let itemType: string | undefined;
  if (Office.context?.mailbox?.item) {
    itemType = Office.context.mailbox.item.itemType;
  }

  const isAppointment = itemType === Office.MailboxEnums.ItemType.Appointment;
  const onBack = () => {
    validateOnFormChanged(() => {
      handleBack();
    });
  };
  const handleBack = () => {
    updateTransaction(null);
    goBack();
  };
  const handleSelectMatter = () => {
    push(ROUTES.MATTER_SELECTION, { returnFocusTo: 'matter' });
  };
  const handleSelectTransactionType = () => {
    push(ROUTES.TRANSACTION_TYPE_SELECTION, {
      onlyHourBasedTransactionTypes: !!transaction?.onlyHourBasedTransactionTypes,
      returnFocusTo: 'transactionType',
    });
  };
  const handleNeedSuggestions = () => {
    push(ROUTES.NEED_SUGGESTIONS, { returnFocusTo: 'subject' });
  };
  const handleSkills = () => {
    push(ROUTES.ADD_SKILLS, { returnFocusTo: 'skill' });
  };
  const handleMatterPhase = () => {
    push(ROUTES.ADD_MATTER_PHASE, { returnFocusTo: 'matterPhase' });
  };
  const handleChangedValue = (key: string, value: unknown, isManualKey: string | null = null) => {
    const payload = {
      ...transaction,
      [key]: value,
      ...(isManualKey && {
        [isManualKey]: true,
      }),
    } as ITransaction;
    updateTransaction({ ...payload });
  };
  const clearMatterPhase = () => {
    updateTransaction({
      ...transaction,
      matterPhase: null,
      matterPhaseId: null,
    } as unknown as ITransaction);
  };
  const handleTransactionisFromMailOrAppointment = async (transactionRecord: ITransaction) => {
    if (!needsReauth) {
      await tagEmailWithCategory(`[CSI - ${t('transaction:transaction')}]`, {
        transactionId: transactionRecord?.id ?? '',
      });
      await untagEmailCategory(new RegExp(String.raw`\[csi - ${t('matter:matter')}`, 'i'), {
        matterId: '',
      });
      await tagEmailWithCategory(
        `[CSI - ${t('matter:matter')} - ${transaction?.matter?.matterNumber}]`,
        {
          matterId: transactionRecord?.matterId,
        },
      );
    }
    if (transaction?.mailAsNote) {
      const { file, subject } = transaction?.mailAsNoteWithoutAttachments
        ? await generateEmlWithoutAttachments(transaction?.currentSubject)
        : await generateEmlFromMail(transaction?.currentSubject);
      createNoteMutation.mutate(
        {
          note: subject,
          transactionId: transactionRecord?.id ?? '',
          significance: SignificanceTypeValue.normal,
        },
        {
          onSuccess: (record: INote) => {
            handleCreateTransactionNoteAttachment(record?.id, file as File);
          },
        },
      );
    } else {
      handleBack();
    }
  };
  const handleCreateTransactionNoteAttachment = (noteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    createNoteAttachmentMutation.mutate(
      {
        notesId: noteId,
        data: formData,
      },
      {
        onError: (error) => {
          const errorMessage =
            error.response?.data?.message || error.message || t('common:somethingWentWrong');
          setNoteAttachmentErrorMessage(errorMessage);
          setShowNoteAttachmentErrorModal(true);
        },
        onSuccess: () => {
          handleBack();
        },
      },
    );
  };
  const checkForTimerAndRemove = () => {
    if (transaction?.timerId) {
      const timers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
      const updatedTimers = timers.filter((t) => t.id !== transaction?.timerId);
      localStorage.setItem(LOCAL_STORAGE_NAMES.timer, JSON.stringify(updatedTimers));
    }
  };
  const {
    validateFields,
    getFirstInvalidField,
    handleEditTransaction,
    handleCreateTransaction,
    transactionFieldError,
    clearTransactionFieldError,
    isTransactionError,
    transactionErrorMessage,
    closeTransactionErrorModal,
  } = useTransactionFormHandling({
    transactionId,
    transactionDate,
    billableFlag,
    parameters,
    hidePricingInfo,
    isTransactionTypeRequired,
    userType: user?.userType ?? '',
    handleBack,
    checkForTimerAndRemove,
    handleTransactionisFromMailOrAppointment,
  });
  const matterErrorId = useId();
  const transactionDateErrorId = useId();
  const {
    errorFieldRef,
    showTransactionDateError,
    matterErrorMessage,
    transactionDateErrorMessage,
  } = useTransactionFieldError({
    transactionFieldError,
    clearTransactionFieldError,
    matterId: transaction?.matter?.id,
    transactionDate,
  });
  const handleTransactionSave = () => {
    if (transactionId) {
      handleEditTransaction();
    } else {
      handleCreateTransaction();
    }
  };
  const focusField = (field: InvalidTransactionField) =>
    formCardRef.current?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus();
  const handleTransactionSubmit = () => {
    const firstInvalidField = getFirstInvalidField();
    if (firstInvalidField) {
      showToast('info', t('common:fillOutAllRequiredFields'));
      focusField(firstInvalidField);
      return;
    }
    if (
      !skipPostingPeriodCheck &&
      user?.userType !== UserType.ADMIN &&
      !transaction?.isSubscriptionBased &&
      parametersDetails?.parameters?.transactionPostingPeriodParameters?.postingPeriodInUse
    ) {
      setCheckTransactionPostingPeriod(true);
      return;
    }
    handleTransactionSave();
  };
  const saveOnShortcut = useSaveShortcut(handleTransactionSubmit, isLoading || loading);
  useEffect(() => {
    trackPage(AnalyticsPage.TRANSACTION_ADD_EDIT);
  }, [trackPage]);
  useEffect(() => {
    if (
      !transactionId &&
      !transaction?.matter?.id &&
      !location.state?.autoNavigatedToMatterSelection
    ) {
      push(ROUTES.MATTER_SELECTION, {
        autoNavigatedToMatterSelection: true,
        returnFocusTo: 'matter',
      });
    }
  }, []);
  useEffect(() => {
    if (!isTransactionLoading) {
      setTransactionDate(transaction?.transactionDate ?? currentDate);
      setTitle(
        <AddEditTransactionHeader
          handleBack={onBack}
          showCross
          validateOnFormChanged={validateOnFormChanged}
          title={!transaction?.id ? t('transaction:addNew') : transaction?.subject}
          subTitle={!transaction?.id ? '' : t('transaction:editTransaction')}
        />,
      );
    }
    if (transaction?.matter?.invoiceableType === InvoiceableType.nonBillable) {
      setBillableFlag(false);
    }
  }, [transaction, transactionId, location?.pathname]);
  const currentMatterId = transaction?.matter?.id;
  const previousMatterIdRef = useRef(currentMatterId);
  // Picking from the matter picker page remounts the form, which derives the flag again; the matter
  // dropdown swaps the matter in place, so the flag has to follow it here.
  useEffect(() => {
    const matterChanged = previousMatterIdRef.current !== currentMatterId;
    previousMatterIdRef.current = currentMatterId;
    if (!matterChanged || transactionId || !transaction?.matter) return;
    setBillableFlag(transaction.matter.invoiceableType !== InvoiceableType.nonBillable);
  }, [currentMatterId]);
  useEffect(() => {
    if (transactionDetails) {
      setTransactionDate(transactionDetails?.transactionDate ?? currentDate);
      setBillableHours((transactionDetails?.billableQuantity ?? 0)?.toFixed(2));
      setWorkedHours((transactionDetails?.workedHours ?? 0)?.toFixed(2));
      if (transactionDetails?.matter?.currency !== parameters?.currency) {
        setUnitPrice((transactionDetails?.unitPriceInCurrency ?? 0)?.toFixed(2));
      } else {
        setUnitPrice((transactionDetails?.unitPrice ?? 0)?.toFixed(2));
      }
      setTotalPrice((transactionDetails?.totalPrice ?? 0)?.toFixed(2));
      setBillableFlag(transactionDetails?.matter?.invoiceableType === InvoiceableType.billable);
      updateTransaction(
        {
          ...transactionDetails,
          currentSubject: transactionDetails?.subject,
        },
        true, // store the value as initial transaction for change detection
      );
    }
  }, [transactionDetails, location?.pathname]);

  useEffect(() => {
    if (suggestedSkill?.skillId && !transaction?.isManualSkill) {
      handleChangedValue('skill', { id: suggestedSkill?.skillId, name: suggestedSkill?.name });
    }
  }, [suggestedSkill]);

  const getMatterMessage = () => {
    if (!transaction?.matter) return null;
    const { invoiceableType } = transaction.matter;
    if (invoiceableType === InvoiceableType.nonBillable) {
      return t('transaction:selectedMatterIsNonBillable');
    }
    if (invoiceableType === InvoiceableType.internal) {
      return t('transaction:selectedMatterIsInternal');
    }
    return null;
  };
  const matterMessage = getMatterMessage();
  const matterRestrictionWarnings = useMatterRestrictionWarnings(transaction?.matter);
  const renderMatterMessages = () => {
    if (!matterRestrictionWarnings?.length && !matterMessage && !matterErrorMessage) return null;
    return (
      <>
        {matterRestrictionWarnings?.map((message) => (
          <InfoMessage key={message} text={message} type="warning" />
        ))}
        {matterMessage && <InfoMessage text={matterMessage} type="help" />}
        {matterErrorMessage && (
          <FieldErrorMessage id={matterErrorId} ref={errorFieldRef} text={matterErrorMessage} />
        )}
      </>
    );
  };
  /**
   * A field borrows its border colour from the message it shows. Where several messages stack up,
   * they are checked in the order they render, so the topmost one is the one that colours the field.
   */
  const getMatterFieldStatus = (): FieldMessageStatus | undefined => {
    if (matterRestrictionWarnings?.length) return 'warning';
    if (matterMessage) return 'help';
    if (matterErrorMessage) return 'error';
    return undefined;
  };
  const matterFieldStatus = getMatterFieldStatus();
  const transactionDateFieldStatus = ((): FieldMessageStatus | undefined => {
    if (transactionDateErrorMessage) return 'error';
    if (lockedPeriodWarningMessage) return 'warning';
    return undefined;
  })();
  const renderTransactionDateMessage = () => {
    if (transactionDateErrorMessage) {
      return (
        <FieldErrorMessage
          id={transactionDateErrorId}
          ref={errorFieldRef}
          text={transactionDateErrorMessage}
        />
      );
    }
    if (lockedPeriodWarningMessage) {
      return <InfoMessage text={lockedPeriodWarningMessage} type="warning" />;
    }
    return null;
  };
  const billableHoursFieldStatus: FieldMessageStatus | undefined = transaction?.roundingRuleApplied
    ? 'help'
    : undefined;
  const renderRoundingRuleMessage = () => {
    if (!transaction?.roundingRuleApplied) return null;
    return (
      <InfoMessage
        data-testid="rounding-rule-applied-message"
        text={t('transaction:roundingRuleApplied')}
        type="help"
      />
    );
  };
  const hasStandardPriceTransactionType = (transaction?.transactionType?.standardPrice ?? 0) > 0;
  const transactionTypeFieldStatus: FieldMessageStatus | undefined = hasStandardPriceTransactionType
    ? 'help'
    : undefined;
  const renderTransactionTypeMessage = () => {
    if (!hasStandardPriceTransactionType) return null;
    return <InfoMessage text={t('transaction:standardPriceTransactionType')} type="help" />;
  };
  const getUnitPriceMessage = () => {
    if (transaction?.isManualUnitPrice) {
      return t('transaction:priceSourceInfo', {
        priceSource: t('transaction:transactionUnitPriceSource.manual'),
      });
    }
    if (
      transaction?.priceSource !== undefined &&
      transaction?.priceSource !== TransactionUnitPriceSource.NOT_SET
    ) {
      return t('transaction:priceSourceInfo', {
        priceSource: t(
          TransactionUnitPriceSourceTranslationMapping[
            transaction?.priceSource as TransactionUnitPriceSource
          ],
        ),
      });
    }
    return null;
  };
  const unitPriceMessage = getUnitPriceMessage();
  const unitPriceFieldStatus: FieldMessageStatus | undefined = unitPriceMessage
    ? 'help'
    : undefined;
  const renderUnitPriceMessage = () => {
    if (!unitPriceMessage) return null;
    return <InfoMessage text={unitPriceMessage} type="help" />;
  };

  const getSubjectMessage = () => {
    if (!transaction?.matter) return null;
    const businessUnitLanguage = userDetails?.businessUnit?.language;
    const matterLanguage = transaction?.matter?.language;
    if (matterLanguage !== businessUnitLanguage) {
      return t('transaction:userBusinessUnitLanguageDifferentThanMatter');
    }
    return null;
  };
  const subjectMessage = getSubjectMessage();
  const subjectFieldStatus: FieldMessageStatus | undefined = subjectMessage ? 'help' : undefined;
  const renderSubjectMessage = () => {
    if (!subjectMessage) return null;
    return <InfoMessage text={subjectMessage} type="help" />;
  };
  const renderTotalPriceMessage = () => {
    if (!transaction?.totalPriceSetManually) return null;
    return (
      <InfoMessage
        data-testid="total-price-set-manually-info"
        text={t('transaction:totalPriceSetManuallyInfo')}
        type="warning"
      />
    );
  };
  const renderMailAsNoteMessage = () => {
    if (!transaction?.mailAsNotewithoutAttachmentsWarning) return null;
    return (
      <InfoMessage
        data-testid="mail-attachments-not-saved-note"
        text={t('transaction:mailAttachmentsNotSavedNote')}
        type="warning"
      />
    );
  };
  const handleSwitchMailAsNote = async (value: boolean) => {
    let mailAsNote = value;
    const item = Office.context.mailbox.item;
    const itemId = item?.itemId ?? (await getItemId(item)) ?? '';
    if (value && itemId) {
      setIsLoading(true);
      const { file, error } = await generateEmlWithoutAttachments(transaction?.subject);
      if (error) {
        setIsLoading(false);
        showToast('error', t(error));
        updateTransaction({
          ...(transaction as ITransaction),
          mailAsNote: false,
        }) as unknown as ITransaction;
        return;
      }
      if (file && file.size > MAX_FILE_SIZE) {
        setIsLoading(false);
        setShowFileSizeWarning(true);
        updateTransaction({
          ...transaction,
          mailAsNote: false,
          mailAsNotewithoutAttachmentsWarning: false,
          mailAsNoteWithoutAttachments: false,
        } as ITransaction);
        return;
      }
      const { file: emlFile } = await generateEmlFromMail(transaction?.currentSubject);
      const checkAttachmentSizes = await checkAttachmentSizeCompliance(itemId).finally(() => {
        setIsLoading(false);
      });
      if (!checkAttachmentSizes || emlFile.size > MAX_FILE_SIZE) {
        setConfirmationForLargeAttachmentsModal(true);
        return;
      }
      mailAsNote = true;
    }
    updateTransaction({
      ...transaction,
      mailAsNote: mailAsNote,
      mailAsNotewithoutAttachmentsWarning: false,
      mailAsNoteWithoutAttachments: false,
    } as ITransaction);
  };
  const validateOnFormChanged = (onProceed: () => void) => {
    const isFormChanged = isTransactionFormChanged(
      transaction,
      initialTransaction,
      !!transactionId,
    );
    setShowDiscardDialog(isFormChanged);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  if (isTransactionLoading) return <Spin fullscreen />;
  return (
    <div className="h-full w-full d-flex flex-col pb-gutter add-transaction-form">
      {(isLoading || loading) && <Spin fullscreen />}
      {showCopyTransactionBanner && (
        <InformativeBanner
          message={copyTransactionBannerMessage}
          onClose={dismissCopyTransactionBanner}
        />
      )}
      <FormCard ref={formCardRef} {...entryMarkers.form}>
        <Form
          layout="vertical"
          className="w-full d-flex fd-column gap-gutter"
          onKeyDown={saveOnShortcut}
        >
          <FormItem
            data-testid="matter-form-item"
            name="matter"
            label={t('matter:matter')}
            required={true}
            validateStatus={fieldStatusValidation(matterFieldStatus)}
            className={fieldStatusClassName(
              matterFieldStatus,
              (transactionId || transaction?.disableMatterChange) && 'disabled',
            )}
            extra={renderMatterMessages()}
          >
            <div className={`relative ${transactionId ? 'disabled-field' : ''}`}>
              <MatterSelect
                disabled={!!transactionId || !!transaction?.disableMatterChange}
                transactionTypeInUse={parameters?.transactionTypeInUse}
                aria-describedby={describedByError(matterErrorMessage, matterErrorId)}
                onOpenPicker={handleSelectMatter}
              />
            </div>
          </FormItem>
          {parameters?.transactionTypeInUse && (
            <FormItem
              data-testid="transaction-type-form-item"
              {...entryMarkers.transactionType}
              name="transactionType"
              label={t('transaction:transactionType.transactionType')}
              required={parameters?.transactionTypeRequired}
              className={fieldStatusClassName(
                transactionTypeFieldStatus,
                isPostingPeriodLocked && 'disabled',
              )}
              extra={renderTransactionTypeMessage()}
            >
              <div className={`relative`}>
                <TransactionTypeSelect
                  disabled={isPostingPeriodLocked}
                  onOpenPicker={handleSelectTransactionType}
                  onClear={
                    isTransactionTypeRequired
                      ? undefined
                      : () => handleChangedValue('transactionType', null)
                  }
                />
              </div>
            </FormItem>
          )}
          <FormItem
            label={t('common:subject')}
            required
            {...entryMarkers.subject}
            className={fieldStatusClassName(subjectFieldStatus)}
            extra={renderSubjectMessage()}
          >
            <div className="relative">
              <TextAreaField
                data-testid="subject-input"
                data-field="subject"
                maxLength={1000}
                value={transaction?.currentSubject ?? ''}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateTransaction({
                    ...transaction,
                    currentSubject: e?.target?.value ?? '',
                    isSubjectSetByUser: true,
                  } as ITransaction);
                }}
                footer={
                  <Flex
                    align="center"
                    gap={2}
                    className="language-wrapper"
                    data-testid="subject-language"
                  >
                    <LanguageOutline className="flex-shrink-0" />
                    <Text $fontsize="10px" $color="description" $lineheight="100%">
                      {displayLanguageName(
                        transaction?.matter?.language ?? getAppLanguageCode(),
                        getAppLanguageCode(),
                      )}
                    </Text>
                  </Flex>
                }
              />
              <Button
                data-testid="need-suggestions-button"
                type="text"
                className="change-value-btn need-suggestion-btn"
                onClick={handleNeedSuggestions}
              >
                {t('transaction:needSuggestions')}
              </Button>
            </div>
          </FormItem>
          <FormItem
            data-testid="transaction-date-form-item"
            label={t('transaction:date')}
            className={fieldStatusClassName(
              transactionDateFieldStatus,
              'relative',
              isPostingPeriodLocked && 'disabled',
            )}
            validateStatus={fieldStatusValidation(transactionDateFieldStatus)}
            extra={renderTransactionDateMessage()}
          >
            <CustomDatePicker
              data-testid="date-picker"
              aria-invalid={Boolean(transactionDateErrorMessage)}
              aria-describedby={describedByError(
                transactionDateErrorMessage,
                transactionDateErrorId,
              )}
              disabled={isPostingPeriodLocked}
              className="w-full"
              placeholder={t('transaction:date')}
              value={transactionDate ? dayjs(transactionDate) : undefined}
              onChange={(date) => {
                const selectedDate = date ? date.format('YYYY-MM-DD') : '';
                setTransactionDate(selectedDate);
                handleChangedValue('transactionDate', selectedDate);
              }}
              allowClear={false}
              suffixIcon={<CalendarOutline className="calendar-icon" />}
              placement="bottomLeft"
              showWeek
              superNextIcon={null}
              superPrevIcon={null}
            />
          </FormItem>
          {!hidePricingInfo &&
            transaction?.matter?.invoiceableType !== InvoiceableType.internal && (
              <div className="d-flex justify-between items-center w-full billable-switch">
                <Heading level={6} $color="title" $fontsize="12px" $weight={400} $lineheight="18px">
                  {t('transaction:billable')}
                </Heading>
                <Switch
                  data-testid="billable-switch"
                  aria-label={t('transaction:billable')}
                  disabled={
                    transaction?.matter?.invoiceableType === InvoiceableType.nonBillable ||
                    isPostingPeriodLocked
                  }
                  value={billableFlag}
                  onChange={(e) => {
                    setBillableFlag(e);
                    handleChangedValue(
                      'invoiceableType',
                      e ? InvoiceableType.billable : InvoiceableType.nonBillable,
                    );
                  }}
                />
              </div>
            )}
          {transaction?.matter?.invoiceableType !== InvoiceableType.internal &&
            (!transaction?.transactionType ||
              transaction.transactionType.standardPrice == null ||
              transaction.transactionType.standardPrice === 0) && (
              <>
                {billableFlag && (
                  <FormItem
                    label={
                      transaction?.unitId && transaction?.unitId !== SystemConstants.UNIT_HOUR
                        ? t('transaction:billableAmount') + ` (${unitTranslationName})`
                        : t('transaction:billableHours')
                    }
                    className={fieldStatusClassName(
                      billableHoursFieldStatus,
                      isPostingPeriodLocked && 'disabled',
                    )}
                    extra={renderRoundingRuleMessage()}
                  >
                    <InputNumber
                      data-testid="billable-hours-input"
                      className="w-full"
                      disabled={isPostingPeriodLocked}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      stringMode
                      value={formatter.regionalNumberFormat({
                        value: Number(billableHours),
                        minimumFractionDigits: 2,
                      })}
                      controls={false}
                      keyboard={false}
                      onKeyDown={numericFieldKeyDown(billableStep, setBillableHours)}
                      onPaste={handleNumericFieldPaste}
                      onBlur={(event) => {
                        const valueStr = event.target.value;
                        if (!valueStr) return;
                        const parsed = parseRegionalNumber(valueStr);
                        if (Number.isNaN(parsed)) return;

                        const numericValue = Number(parsed.toFixed(2));
                        if (numericValue <= MAX_LIMIT_VALUE_TRANSACTION) {
                          const {
                            roundedAmount: roundedHourPrice,
                            roundingApplied: serverPriceRounded,
                          } = handleHourPriceRounding(
                            String(numericValue),
                            isTransactionTypeHourBased,
                            transaction,
                            matterTransactionParameterDetail,
                            parameters,
                          );
                          const formatted = String(roundedHourPrice);
                          let payload = {
                            ...transaction,
                            billableQuantity: roundedHourPrice,
                            isManualBillableQuantity: true,
                            roundingRuleApplied: serverPriceRounded,
                          } as unknown as ITransaction;
                          setBillableHours(formatted);
                          if (!transaction?.isManualWorkedHours) {
                            const billableQuantity = formatted;
                            setWorkedHours(billableQuantity);
                            payload = {
                              ...payload,
                              workedHours: billableQuantity,
                            } as unknown as ITransaction;
                            updateTransaction({ ...payload });
                          } else {
                            updateTransaction({ ...payload } as ITransaction);
                          }
                        }
                      }}
                    />
                  </FormItem>
                )}

                {billableFlag && !hidePricingInfo && (
                  <FormItem
                    label={
                      (transaction?.unitId && transaction?.unitId !== SystemConstants.UNIT_HOUR
                        ? t('transaction:unitPrice')
                        : t('transaction:hourPrice')) +
                      ` (${transaction?.matter?.currency ?? parameters?.currency ?? ''})`
                    }
                    className={fieldStatusClassName(
                      unitPriceFieldStatus,
                      isPostingPeriodLocked && 'disabled',
                    )}
                    extra={renderUnitPriceMessage()}
                  >
                    <InputNumber
                      className="w-full "
                      data-testid="unit-price-input"
                      tabIndex={-1}
                      disabled={isPostingPeriodLocked}
                      placeholder="0.00"
                      value={formatter.regionalNumberFormat({
                        value: Number(unitPrice),
                        minimumFractionDigits: 2,
                      })}
                      min="0"
                      step="0.01"
                      stringMode
                      controls={false}
                      keyboard={false}
                      onKeyDown={numericFieldKeyDown(PRICE_STEP, setUnitPrice)}
                      onPaste={handleNumericFieldPaste}
                      onBlur={(value) => {
                        if (value.target.value !== null) {
                          const parsed = parseRegionalNumber(value.target.value);
                          if (Number.isNaN(parsed)) return;
                          const formatted = parsed.toFixed(2);
                          const hasValueChanged = Number(unitPrice ?? 0) !== Number(formatted);
                          setUnitPrice(formatted);
                          const payload = {
                            ...transaction,
                            unitPrice: formatted,
                            isManualUnitPrice: hasValueChanged,
                          } as unknown as ITransaction;
                          updateTransaction({ ...payload });
                        } else {
                          const zeroValue = (0).toFixed(2);
                          setUnitPrice(zeroValue);
                          handleChangedValue('unitPrice', zeroValue);
                        }
                      }}
                    />
                  </FormItem>
                )}
              </>
            )}
          {((transaction?.transactionType &&
            transaction?.transactionType?.standardPrice !== 0 &&
            transaction?.transactionType?.standardPrice !== null) ||
            transaction?.totalPriceSetManually) && (
            <FormItem
              label={`${t('transaction:totalPrice')} (${transaction?.matter?.currency ?? parameters?.currency ?? ''})`}
              className="disabled"
              extra={renderTotalPriceMessage()}
            >
              <InputNumber
                data-testid="total-price-input"
                disabled
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full "
                value={
                  transaction?.matter?.invoiceableType !== InvoiceableType.nonBillable &&
                  billableFlag
                    ? formatter.regionalNumberFormat({
                        value: Number(totalPrice ?? 0),
                        minimumFractionDigits: 2,
                      })
                    : (0).toFixed(2)
                }
              />
            </FormItem>
          )}
          {transaction?.isFromMailOrAppointment && !isAppointment && (
            <FormItem extra={renderMailAsNoteMessage()}>
              <div className="d-flex justify-between items-center w-full billable-switch">
                <Heading level={6} $color="title" $fontsize="12px" $weight={400} $lineheight="18px">
                  {t('transaction:addThisMailAsNote')}
                </Heading>
                <Switch
                  data-testid="mail-as-note-switch"
                  aria-label={t('transaction:addThisMailAsNote')}
                  disabled={isPostingPeriodLocked || needsReauth}
                  value={transaction?.mailAsNote}
                  onChange={(e) => {
                    handleSwitchMailAsNote(e);
                  }}
                />
              </div>
            </FormItem>
          )}
          <FormItem
            label={t('transaction:workedHours')}
            className={isPostingPeriodLocked ? 'disabled' : undefined}
          >
            <InputNumber
              data-testid="worked-hours-input"
              className="w-full"
              tabIndex={-1}
              disabled={isPostingPeriodLocked}
              placeholder="0.00"
              value={formatter.regionalNumberFormat({
                value: Number(workedHours),
                minimumFractionDigits: 2,
              })}
              min="0"
              step="0.01"
              stringMode
              controls={false}
              keyboard={false}
              onKeyDown={numericFieldKeyDown(hoursStep, setWorkedHours)}
              onPaste={handleNumericFieldPaste}
              onBlur={(value) => {
                if (value.target.value !== null) {
                  const parsed = parseRegionalNumber(value.target.value);
                  if (Number.isNaN(parsed)) return;
                  const formatted = parsed.toFixed(2);
                  setWorkedHours(formatted);
                  handleChangedValue('workedHours', formatted, 'isManualWorkedHours');
                } else {
                  setWorkedHours('0.00');
                  handleChangedValue('workedHours', '0.00');
                }
              }}
            />
          </FormItem>
          <FormItem
            label={t('transaction:requiredSkill')}
            name="skill"
            required={parameters?.skillRequired}
            rules={[
              {
                required: parameters?.skillRequired,
                message: t('transaction:selectSkill'),
              },
            ]}
            className={isPostingPeriodLocked ? 'disabled' : undefined}
          >
            <div className="relative">
              <SkillSelect
                disabled={isPostingPeriodLocked}
                onOpenPicker={handleSkills}
                onClear={
                  isSkillRequiredOrUnknown
                    ? undefined
                    : () => handleChangedValue('skill', null, 'isManualSkill')
                }
              />
            </div>
          </FormItem>
          <FormItem
            label={t('transaction:matterPhase')}
            name="matterphase"
            className={isMatterPhaseLocked ? 'disabled' : undefined}
          >
            <div className="relative">
              <MatterPhaseSelect
                disabled={isMatterPhaseLocked}
                onOpenPicker={handleMatterPhase}
                onClear={clearMatterPhase}
              />
            </div>
          </FormItem>
          <PinnedSaveButton
            disabled={validateFields()}
            onClick={handleTransactionSubmit}
            data-testid="submit-transaction-button"
          >
            {transactionId ? t('common:saveChanges') : t('transaction:addTransaction')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      {checkTransactionPostingPeriod && (
        <TransactionPostingPeriod
          checkTransactionPostingPeriod={checkTransactionPostingPeriod}
          transactionDate={transactionDate ?? ''}
          parametersDetails={parametersDetails}
          onClose={(saveTransactionFlag?: boolean) => {
            setCheckTransactionPostingPeriod(false);
            if (saveTransactionFlag) {
              handleTransactionSave();
            }
          }}
          onPeriodUnavailable={(message) => {
            setCheckTransactionPostingPeriod(false);
            showTransactionDateError(message);
          }}
        />
      )}
      <CustomModal
        data-testid="large-attachments-modal"
        visible={confirmationForLargeAttachmentsModal}
        title={t('common:question')}
        subdescription={t('transaction:attachmentTooLargeForTransactionNote')}
        confirmText={t('common:yes')}
        cancelText={t('common:no')}
        icon={<MessageQuestion />}
        onConfirm={() => {
          updateTransaction({
            ...transaction,
            mailAsNote: true,
            mailAsNoteWithoutAttachments: true,
            mailAsNotewithoutAttachmentsWarning: true,
          } as ITransaction);
          setConfirmationForLargeAttachmentsModal(false);
        }}
        onClose={() => {
          updateTransaction({
            ...transaction,
            mailAsNote: false,
            mailAsNoteWithoutAttachments: false,
          } as ITransaction);
          setConfirmationForLargeAttachmentsModal(false);
        }}
      />
      <CustomModal
        data-testid="file-size-warning-modal"
        visible={showFileSizeWarning}
        title={t('common:error')}
        subdescription={t('transaction:emailSizeExceedsLimit')}
        confirmText={t('common:ok')}
        icon={<LinearCloseCirlce />}
        onClose={() => {
          setShowFileSizeWarning(false);
        }}
        onConfirm={() => {
          setShowFileSizeWarning(false);
        }}
      />
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
      <CustomModal
        data-testid="note-attachment-error-modal"
        visible={showNoteAttachmentErrorModal}
        title={t('common:error')}
        subdescription={noteAttachmentErrorMessage}
        confirmText={t('common:ok')}
        icon={<LinearCloseCirlce />}
        onConfirm={() => {
          setShowNoteAttachmentErrorModal(false);
          handleBack();
        }}
        onClose={() => {
          setShowNoteAttachmentErrorModal(false);
          handleBack();
        }}
      />
      <ApiErrorModal
        data-testid="transaction-error-modal"
        visible={isTransactionError}
        message={transactionErrorMessage}
        onClose={closeTransactionErrorModal}
      />
    </div>
  );
};

export default AddTransaction;
